import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppError, MetaInfo } from '../types';

/**
 * Interface representing the shape of the API state
 * - Tracks multiple API requests by unique keys
 * - Stores request status, data, errors, and metadata
 */
interface ApiState {
  [key: string]: {
    requesting: boolean; // Whether the request is in progress
    timeout?: number; // Optional timeout ID
    abortController?: AbortController; // Controller for cancelling requests
    data?: any; // Successful response data
    statusCode?: number; // HTTP status code
    error?: AppError; // Error information
    meta?: MetaInfo; // Additional metadata about the response
  };
}

// Initial empty state
const initialState: ApiState = {};

/**
 * Redux slice for managing API request states
 * - Handles tracking of request lifecycle (start/success/failure)
 * - Supports request cancellation
 * - Maintains clean state management
 */
const apiSlice = createSlice({
  name: 'api', // Slice name
  initialState, // Initial state
  reducers: {
    /**
     * Action for when a request starts
     * - Sets requesting flag to true
     * - Stores abort controller for cancellation
     * - Clears any previous errors
     */
    fetchStart: (
      state,
      action: PayloadAction<{ key: string; abortController: AbortController }>
    ) => {
      const { key, abortController } = action.payload;
      state[key] = {
        ...state[key],
        requesting: true,
        abortController,
      };
      delete state[key].error;
    },

    /**
     * Action for successful request completion
     * - Sets requesting flag to false
     * - Stores response data and metadata
     */
    fetchSuccess: (
      state,
      action: PayloadAction<{
        key: string;
        data: any;
        statusCode: number;
        meta: MetaInfo;
      }>
    ) => {
      const { key, data, statusCode, meta } = action.payload;
      state[key] = {
        requesting: false,
        data,
        statusCode,
        meta,
      };
    },

    /**
     * Action for failed requests
     * - Sets requesting flag to false
     * - Stores error information
     */
    fetchFailed: (
      state,
      action: PayloadAction<{
        key: string;
        error: AppError;
        statusCode?: number;
      }>
    ) => {
      const { key, error, statusCode } = action.payload;
      state[key] = {
        requesting: false,
        error,
        statusCode,
      };
    },

    /**
     * Action to reset/clear a request state
     * - Removes the request entry from state
     */
    fetchReset: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      delete state[key];
    },
  },
});

// Export generated action creators
export const { fetchStart, fetchSuccess, fetchFailed, fetchReset } =
  apiSlice.actions;

// Export the reducer
export const api = apiSlice.reducer;
